/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * BlobStore — IndexedDB-backed binary/base64 storage.
 *
 * localStorage has a ~5 MB quota. Campaign submissions embed base64 images
 * (item photos, chat attachments) that easily exceed that limit.
 *
 * This module moves every large base64 string into an IndexedDB object store
 * and replaces it with a lightweight reference key (`blob://<uuid>`) so the
 * JSON stored in localStorage stays small.
 *
 * Usage:
 *   // Before saving to localStorage
 *   const slim = await BlobStore.externalize(submission);
 *
 *   // After reading from localStorage
 *   const full = await BlobStore.hydrate(slimSubmission);
 */

const DB_NAME    = 'dm_blob_store';
const DB_VERSION = 1;
const STORE_NAME = 'blobs';
const BLOB_PREFIX = 'blob://';

// Minimum size (chars) to externalize — small data URLs stay inline
const MIN_EXTERNALIZE_SIZE = 1024; // 1 KB

// ── IndexedDB Helpers ────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function putBlob(key: string, data: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put(data, key);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror    = () => { db.close(); reject(tx.error); };
  });
}

async function getBlob(key: string): Promise<string | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.get(key);
    req.onsuccess = () => { db.close(); resolve(req.result as string | undefined); };
    req.onerror   = () => { db.close(); reject(req.error); };
  });
}

async function deleteBlob(key: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(key);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror    = () => { db.close(); reject(tx.error); };
  });
}

// ── Utility: is this string a blob reference? ────────────────────────────

function isBlobRef(v: unknown): v is string {
  return typeof v === 'string' && v.startsWith(BLOB_PREFIX);
}

function isLargeBase64(v: unknown): v is string {
  return typeof v === 'string' && v.length >= MIN_EXTERNALIZE_SIZE && v.startsWith('data:');
}

// ── Public API ───────────────────────────────────────────────────────────

export const BlobStore = {
  /**
   * Replace large base64 fields in a campaign submission with blob:// refs
   * and persist the binary data into IndexedDB.
   * Returns a shallow-cloned submission safe for localStorage.
   */
  async externalize<T extends Record<string, any>>(submission: T): Promise<T> {
    const clone = { ...submission } as any;

    // 1. Item imageUrl fields
    if (Array.isArray(clone.items)) {
      clone.items = await Promise.all(
        (clone.items as any[]).map(async (item: any) => {
          if (isLargeBase64(item.imageUrl)) {
            const key = `${BLOB_PREFIX}${crypto.randomUUID()}`;
            await putBlob(key, item.imageUrl);
            return { ...item, imageUrl: key };
          }
          return item;
        })
      );
    }

    // 2. Discussion attachment data
    if (Array.isArray(clone.discussion)) {
      clone.discussion = await Promise.all(
        (clone.discussion as any[]).map(async (msg: any) => {
          if (isLargeBase64(msg.attachmentData)) {
            const key = `${BLOB_PREFIX}${crypto.randomUUID()}`;
            await putBlob(key, msg.attachmentData);
            return { ...msg, attachmentData: key };
          }
          return msg;
        })
      );
    }

    return clone as T;
  },

  /**
   * Re-hydrate blob:// references back to full base64 data from IndexedDB.
   * Returns a new object with all blob refs resolved.
   */
  async hydrate<T extends Record<string, any>>(submission: T): Promise<T> {
    const clone = { ...submission } as any;

    // 1. Item imageUrl
    if (Array.isArray(clone.items)) {
      clone.items = await Promise.all(
        (clone.items as any[]).map(async (item: any) => {
          if (isBlobRef(item.imageUrl)) {
            const data = await getBlob(item.imageUrl);
            return { ...item, imageUrl: data ?? '' };
          }
          return item;
        })
      );
    }

    // 2. Discussion attachment data
    if (Array.isArray(clone.discussion)) {
      clone.discussion = await Promise.all(
        (clone.discussion as any[]).map(async (msg: any) => {
          if (isBlobRef(msg.attachmentData)) {
            const data = await getBlob(msg.attachmentData);
            return { ...msg, attachmentData: data ?? '' };
          }
          return msg;
        })
      );
    }

    return clone as T;
  },

  /**
   * Collect and delete all blob refs in a submission (cleanup on delete).
   */
  async cleanup<T extends Record<string, any>>(submission: T): Promise<void> {
    const keys: string[] = [];

    if (Array.isArray((submission as any).items)) {
      for (const item of (submission as any).items) {
        if (isBlobRef(item.imageUrl)) keys.push(item.imageUrl);
      }
    }
    if (Array.isArray((submission as any).discussion)) {
      for (const msg of (submission as any).discussion) {
        if (isBlobRef(msg.attachmentData)) keys.push(msg.attachmentData);
      }
    }

    await Promise.all(keys.map(k => deleteBlob(k)));
  },

  /** Store a single blob by key */
  putBlob,

  /** Delete a single blob by key */
  deleteBlob,

  /** Get a single blob by key */
  getBlob,

  /** Check if a value is a blob reference */
  isBlobRef,

  /** The blob:// prefix constant */
  BLOB_PREFIX,

  /** Minimum size threshold for externalizing */
  MIN_EXTERNALIZE_SIZE,
};
